import React, { useState } from 'react';
import {
  FiUser,
  FiBriefcase,
  FiShoppingBag,
  FiCoffee,
  FiCode,
  FiCalendar,
  FiHeart,
  FiHome,
  FiCreditCard,
  FiSmartphone,
  FiShield,
  FiSearch,
  FiChevronRight,
  FiChevronDown,
  FiArrowLeft,
  FiBookOpen,
  FiHelpCircle,
  FiSettings,
  FiUsers,
  FiGrid,
  FiStar,
  FiTrendingUp,
  FiPackage,
  FiFileText,
  FiDollarSign,
  FiBarChart2,
  FiGlobe,
  FiZap,
  FiLayers,
  FiTag,
  FiClipboard,
  FiCheck,
  FiMessageCircle,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

// ─────────────────── Types ───────────────────

interface ProfileGuide {
  id: string;
  icon: React.ReactNode;
  nome: string;
  subtitulo: string;
  cor: string;
  corBg: string;
  descricao: string;
  seccoes: Section[];
}

interface Section {
  titulo: string;
  icon: React.ReactNode;
  passos: Step[];
}

interface Step {
  titulo: string;
  conteudo: string;
}

// ─────────────────── Data ───────────────────

const profiles: ProfileGuide[] = [
  {
    id: 'particular',
    icon: <FiUser />,
    nome: 'Particular',
    subtitulo: 'Para utilizadores individuais',
    cor: 'text-blue-600',
    corBg: 'bg-blue-50 border-blue-200',
    descricao: 'Guia completo para quem usa o Pagali no dia-a-dia: pagar contas, carregar a Wallet, comprar bilhetes, fazer donativos e muito mais.',
    seccoes: [
      {
        titulo: 'Primeiros Passos',
        icon: <FiSmartphone />,
        passos: [
          { titulo: 'Criar conta', conteudo: 'Abre a app Pagali ou acede a pagali.cv. Clica em "Registar", introduz o teu email e cria uma palavra-passe segura. Recebes um email de confirmação — clica no link para ativar a tua conta.' },
          { titulo: 'Completar perfil', conteudo: 'Após o registo, preenche o teu nome completo, telefone e data de nascimento. Ter o perfil completo desbloqueia limites de transação mais altos e funcionalidades adicionais.' },
          { titulo: 'Verificar identidade (CNI)', conteudo: 'Para desbloquear todas as funcionalidades (limites altos, transferências, saques), envia uma foto do teu Cartao Nacional de Identificacao nas definições. A verificação demora até 24 horas.' },
        ],
      },
      {
        titulo: 'Wallet (Carteira Digital)',
        icon: <FiCreditCard />,
        passos: [
          { titulo: 'Carregar a Wallet', conteudo: 'Vai a Wallet > Recarregar. Escolhe o valor e paga com o teu cartao Vinti4 (rede SISP). O saldo fica disponível em segundos após a confirmação 3DS.' },
          { titulo: 'Consultar saldo e histórico', conteudo: 'Na Wallet, o teu saldo atual aparece no topo. Usa o tab "Histórico" para ver todas as transações — filtras por tipo (topup, pagamento, transferência, cashback) e por período.' },
          { titulo: 'Pagar com a Wallet', conteudo: 'Sempre que fores pagar um serviço, bilhete ou produto, escolhe "Pagali Wallet" como método de pagamento. O valor e debitado automaticamente do teu saldo.' },
          { titulo: 'Transferir para amigos', conteudo: 'Vai a Wallet > Transferir. Introduz o email ou telefone do destinatário, o valor e uma nota opcional. Transferências entre Wallets Pagali são gratuitas e instantâneas.' },
        ],
      },
      {
        titulo: 'Pagamentos de Serviços',
        icon: <FiZap />,
        passos: [
          { titulo: 'Pagar eletricidade (Electra)', conteudo: 'Vai a Pagamentos > Eletricidade. Introduz o número do teu contrato ou contador Electra, confirma o valor e paga com a Wallet ou Vinti4. Recebes comprovativo digital.' },
          { titulo: 'Recarregar telemóvel', conteudo: 'Em Pagamentos > Recargas, escolhe a operadora (CV Movel, Unitel T+), introduz o número e o valor. A recarga e creditada em segundos.' },
          { titulo: 'Pagar agua e outros', conteudo: 'O Pagali suporta pagamentos de agua, telecomunicacoes, seguros e taxas. Navega pela lista de serviços em Pagamentos para ver todas as opções disponíveis.' },
        ],
      },
      {
        titulo: 'Eventos e Bilhetes',
        icon: <FiCalendar />,
        passos: [
          { titulo: 'Descobrir eventos', conteudo: 'No menu Eventos, ve concertos, festivais, conferencias e eventos culturais em Cabo Verde. Usa os filtros por categoria, data e localizacao.' },
          { titulo: 'Comprar bilhetes', conteudo: 'Seleciona o evento, escolhe o tipo de bilhete e a quantidade, e finaliza a compra. Recebes um QR Code unico na app para check-in no dia do evento.' },
          { titulo: 'Oferecer bilhetes', conteudo: 'Após a compra, podes partilhar bilhetes com amigos enviando o codigo por mensagem ou email.' },
        ],
      },
      {
        titulo: 'Doações',
        icon: <FiHeart />,
        passos: [
          { titulo: 'Fazer um donativo', conteudo: 'No menu Doações, navega pelas campanhas ativas. Escolhe a causa, define o valor e paga com Wallet ou Vinti4. Recebes recibo digital da doação.' },
          { titulo: 'Acompanhar campanhas', conteudo: 'Cada campanha mostra o progresso em relacao ao objetivo, número de doadores e atualizações do organizador.' },
        ],
      },
      {
        titulo: 'Fidelidade e Pontos',
        icon: <FiStar />,
        passos: [
          { titulo: 'Ganhar pontos', conteudo: 'Ganhas pontos automaticamente: 1 ponto por cada 10 ECV pagos pela Wallet, 1 ponto por cada 100 ECV carregados, 500 pontos por convidar um amigo, 250 pontos por aceitar um convite.' },
          { titulo: 'Niveis de fidelidade', conteudo: 'Existem 4 niveis: Bronze (0 pts), Silver (1.000 pts, 1% cashback), Gold (5.000 pts, 2% cashback) e Platinum (15.000 pts, 3% cashback). Sobes de nível automaticamente ao acumular pontos.' },
          { titulo: 'Cashback', conteudo: 'A partir do nível Silver, cada pagamento feito pela Wallet devolve-te uma percentagem do valor em cashback, creditado automaticamente no teu saldo.' },
          { titulo: 'Convidar amigos', conteudo: 'No Hub > Referidos, partilha o teu codigo de convite. Quando o teu amigo se regista e aplica o codigo, ambos recebem pontos imediatamente.' },
        ],
      },
      {
        titulo: 'Seguranca',
        icon: <FiShield />,
        passos: [
          { titulo: 'Autenticação de dois fatores', conteudo: 'Ativa a autenticação de dois fatores nas definições da conta para proteger o teu acesso com um codigo adicional alem da palavra-passe.' },
          { titulo: 'PIN da Wallet', conteudo: 'Configura um PIN de 4 dígitos para a Wallet em Wallet > Definições > PIN. O PIN e pedido antes de cada pagamento e transferência.' },
          { titulo: 'Recuperar palavra-passe', conteudo: 'No ecra de login, clica "Esqueci a minha palavra-passe". Recebes um email com um link para definir uma nova. Se nao tiveres acesso ao email, contacta o suporte.' },
        ],
      },
    ],
  },
  {
    id: 'empresa',
    icon: <FiBriefcase />,
    nome: 'Empresa',
    subtitulo: 'Para empresas e negocios',
    cor: 'text-indigo-600',
    corBg: 'bg-indigo-50 border-indigo-200',
    descricao: 'Guia para empresas que querem receber pagamentos, gerir serviços, emitir faturas e acompanhar vendas no painel Portal.',
    seccoes: [
      {
        titulo: 'Registo Empresarial',
        icon: <FiBriefcase />,
        passos: [
          { titulo: 'Registar empresa', conteudo: 'Acede a pagali.cv/business/register. Preenche os dados da empresa (nome, NIF, endereco, contacto), escolhe o tipo de conta (empresa, loja, restaurante, promotor) e submete para aprovação.' },
          { titulo: 'Verificação e aprovação', conteudo: 'Após o registo, a equipa Pagali verifica os dados da empresa em até 48 horas. Recebes notificação por email quando a conta for aprovada.' },
          { titulo: 'Aceder ao Portal', conteudo: 'Após aprovação, faz login normalmente — o menu lateral mostra as opções do Portal empresarial alem das funcionalidades de consumidor.' },
        ],
      },
      {
        titulo: 'Dashboard e Relatorios',
        icon: <FiBarChart2 />,
        passos: [
          { titulo: 'Dashboard', conteudo: 'O Portal Dashboard mostra vendas do dia, do mes, total de produtos, pedidos pendentes e graficos de evolução. Atualiza em tempo real.' },
          { titulo: 'Relatorios de vendas', conteudo: 'Em Portal > Relatorios, gera relatorios por período, categoria ou produto. Exporta em CSV ou PDF para contabilidade.' },
        ],
      },
      {
        titulo: 'Gestão de Serviços',
        icon: <FiSettings />,
        passos: [
          { titulo: 'Criar serviços', conteudo: 'Em Portal > Serviços, clica "Novo Serviço". Define o nome, descrição, preco, categoria e imagem. Os serviços ficam disponíveis para os clientes Pagali pagarem.' },
          { titulo: 'Gerir pagamentos recebidos', conteudo: 'Em Portal > Pagamentos, ve todas as transações recebidas dos teus clientes. Filtra por estado (pago, pendente, falhado) e por período.' },
        ],
      },
      {
        titulo: 'Vouchers e Promocoes',
        icon: <FiTag />,
        passos: [
          { titulo: 'Criar vouchers', conteudo: 'Em Portal > Vouchers, cria codigos de desconto para os teus clientes: define o tipo (percentagem ou valor fixo), validade, limite de utilizacoes e produtos aplicaveis.' },
          { titulo: 'Scanner de vouchers', conteudo: 'Usa Portal > Vouchers > Scanner para validar vouchers apresentados em loja. Basta apontar a camera ao QR Code do voucher.' },
        ],
      },
      {
        titulo: 'API para Developers',
        icon: <FiCode />,
        passos: [
          { titulo: 'Obter chave API', conteudo: 'Em Portal > API, gera a tua chave API (api_key). Usa-a para integrar pagamentos Pagali no teu website ou aplicacao própria.' },
          { titulo: 'Documentacao', conteudo: 'A documentacao completa da API REST esta disponível em pagali.cv/programadores. Inclui endpoints, exemplos de codigo e ambiente sandbox para testes.' },
        ],
      },
    ],
  },
  {
    id: 'loja',
    icon: <FiShoppingBag />,
    nome: 'Loja / Comercio',
    subtitulo: 'Para lojas e comercio eletronico',
    cor: 'text-orange-600',
    corBg: 'bg-orange-50 border-orange-200',
    descricao: 'Guia para lojas que vendem produtos online através da plataforma de e-commerce integrada no Pagali.',
    seccoes: [
      {
        titulo: 'Catalogo de Produtos',
        icon: <FiPackage />,
        passos: [
          { titulo: 'Adicionar produtos', conteudo: 'Em Portal > Produtos, clica "Novo Produto". Preenche nome, descrição, preco, categoria, imagens e stock disponível. Podes ter variações (tamanho, cor) por produto.' },
          { titulo: 'Gerir categorias', conteudo: 'Em Portal > Categorias, organiza os teus produtos em categorias e subcategorias. Cada categoria pode ter uma imagem e descrição própria.' },
          { titulo: 'Gerir stock (inventario)', conteudo: 'Em Portal > Inventario, controla o stock de cada produto e variação. Define alertas de stock baixo para seres notificado quando um produto estiver a acabar.' },
          { titulo: 'Variações de produto', conteudo: 'Em Portal > Variações, cria opções como tamanho (S/M/L/XL), cor, material, etc. Associa variações a produtos para que os clientes possam escolher.' },
        ],
      },
      {
        titulo: 'Gestão de Pedidos',
        icon: <FiClipboard />,
        passos: [
          { titulo: 'Receber pedidos', conteudo: 'Quando um cliente compra na tua loja, recebes uma notificação. O pedido aparece em Portal > Pedidos com estado "Novo".' },
          { titulo: 'Processar pedidos', conteudo: 'Abre o pedido, verifica os itens, confirma o pagamento e marca como "Em preparacao". Quando estiver pronto, marca como "Enviado" ou "Pronto para recolha".' },
          { titulo: 'Publicidade', conteudo: 'Em Portal > Publicidade, cria anuncios destacados para os teus produtos aparecerem em posicoes de destaque na loja do Pagali.' },
        ],
      },
    ],
  },
  {
    id: 'restaurante',
    icon: <FiCoffee />,
    nome: 'Restaurante',
    subtitulo: 'Para restauracao e Pagali Food',
    cor: 'text-amber-600',
    corBg: 'bg-amber-50 border-amber-200',
    descricao: 'Guia para restaurantes que usam o Pagali Food para menu digital, pedidos a mesa e delivery.',
    seccoes: [
      {
        titulo: 'Configurar Restaurante',
        icon: <FiCoffee />,
        passos: [
          { titulo: 'Perfil do restaurante', conteudo: 'Regista a tua empresa como tipo "Restaurante/Food". No portal, completa o perfil com: nome, endereco, horario de funcionamento, tipo de cozinha, fotos do espaco e logotipo.' },
          { titulo: 'Criar o menu digital (iMenu)', conteudo: 'Em Portal > Produtos, adiciona os teus pratos como produtos. Cada prato tem: nome, descrição, preco, categoria (entradas, pratos principais, sobremesas, bebidas), foto e disponibilidade.' },
          { titulo: 'Configurar delivery', conteudo: 'Define a zona de entrega, taxa de entrega, tempo estimado e horarios de delivery. Os clientes podem encomendar diretamente pela app Pagali Food.' },
        ],
      },
      {
        titulo: 'Operacao Diaria',
        icon: <FiGrid />,
        passos: [
          { titulo: 'Receber pedidos', conteudo: 'Pedidos dos clientes (presencial via QR Code ou delivery) chegam ao teu Portal > Pedidos em tempo real. Aceita, recusa ou marca como "Em preparacao".' },
          { titulo: 'QR Code de mesa', conteudo: 'Gera QR Codes únicos para cada mesa do restaurante. O cliente lê o QR Code, consulta o menu e faz o pedido diretamente pelo telemóvel — sem esperar pelo empregado.' },
          { titulo: 'Pagamento a mesa', conteudo: 'O cliente pode pagar a conta pelo telemóvel (Wallet Pagali ou Vinti4) sem precisar de chamar o empregado. Recebes a confirmação no portal.' },
        ],
      },
      {
        titulo: 'Equipa',
        icon: <FiUsers />,
        passos: [
          { titulo: 'Gerir empregados', conteudo: 'Adiciona contas para cozinha (Chef), empregados de mesa (Garcon) e estafetas (Motorista). Cada perfil ve apenas o que precisa no portal.' },
        ],
      },
    ],
  },
  {
    id: 'promotor',
    icon: <FiCalendar />,
    nome: 'Promotor de Eventos',
    subtitulo: 'Para quem organiza eventos',
    cor: 'text-purple-600',
    corBg: 'bg-purple-50 border-purple-200',
    descricao: 'Guia para promotores que criam, vendem bilhetes e gerem eventos na plataforma Pagali Events.',
    seccoes: [
      {
        titulo: 'Criar Evento',
        icon: <FiCalendar />,
        passos: [
          { titulo: 'Novo evento', conteudo: 'Em Portal > Eventos, clica "Novo Evento". Preenche: titulo, descrição, data/hora, local, categoria (concerto, festival, conferencia, desporto, etc.), imagem de capa e capacidade maxima.' },
          { titulo: 'Tipos de bilhete', conteudo: 'Configura diferentes tipos de bilhete: Normal, VIP, Early Bird, Grupo, etc. Define o preco, quantidade disponível e datas de venda para cada tipo.' },
          { titulo: 'Eventos recorrentes', conteudo: 'Para eventos que se repetem (semanais, mensais), usa a opcao de recorrencia na criação. O sistema cria automaticamente as datas futuras.' },
          { titulo: 'Submeter para aprovação', conteudo: 'Após preencher tudo, submete o evento para revisão. A equipa Pagali verifica e publica normalmente em até 24 horas.' },
        ],
      },
      {
        titulo: 'Gestão do Evento',
        icon: <FiBarChart2 />,
        passos: [
          { titulo: 'Acompanhar vendas', conteudo: 'No painel do evento, ve em tempo real: bilhetes vendidos, receita total, bilhetes por tipo e taxa de ocupacao. Exporta relatorios em CSV.' },
          { titulo: 'Check-in no dia', conteudo: 'No dia do evento, usa o scanner do Pagali (Portal > Scanner) para ler os QR Codes dos bilhetes. O sistema valida automaticamente e impede entradas duplicadas.' },
          { titulo: 'Gamificacao e desafios', conteudo: 'Cria desafios para os participantes (check-ins, partilhas, quiz) com recompensas em pontos ou descontos. Aumenta o engagement do evento.' },
        ],
      },
    ],
  },
  {
    id: 'ong',
    icon: <FiHeart />,
    nome: 'ONG / Associacao',
    subtitulo: 'Para organizacoes sem fins lucrativos',
    cor: 'text-rose-600',
    corBg: 'bg-rose-50 border-rose-200',
    descricao: 'Guia para ONGs e associações que criam campanhas de angariação de fundos e recebem donativos.',
    seccoes: [
      {
        titulo: 'Campanhas de Doação',
        icon: <FiHeart />,
        passos: [
          { titulo: 'Criar campanha', conteudo: 'Em Portal > Doações, clica "Nova Campanha". Define: titulo, historia/descrição, objetivo financeiro (meta), data limite, imagens e video. Escolhe a categoria (saude, educação, emergência, comunidade, etc.).' },
          { titulo: 'Partilhar campanha', conteudo: 'Cada campanha tem um link unico e QR Code para partilhar em redes sociais, email e WhatsApp. Quanto mais partilhares, mais alcance tens.' },
          { titulo: 'Acompanhar doações', conteudo: 'No dashboard da campanha, ve o progresso em tempo real: montante angariado, número de doadores, doação media e grafico de evolução.' },
          { titulo: 'Transparencia', conteudo: 'Os doadores podem ver como os fundos são usados através das atualizações que publicas na pagina da campanha.' },
        ],
      },
    ],
  },
  {
    id: 'governo',
    icon: <FiHome />,
    nome: 'Governo',
    subtitulo: 'Para entidades governamentais',
    cor: 'text-teal-600',
    corBg: 'bg-teal-50 border-teal-200',
    descricao: 'Guia para entidades governamentais que cobram taxas, impostos e serviços publicos através do Pagali.',
    seccoes: [
      {
        titulo: 'Serviços Públicos',
        icon: <FiHome />,
        passos: [
          { titulo: 'Registar serviços', conteudo: 'Em Portal > Serviços, regista os serviços publicos cobraveis: taxas municipais, emolumentos, licenças, certidões, etc. Define o nome, valor (fixo ou variavel) e categoria.' },
          { titulo: 'Receber pagamentos', conteudo: 'Os cidadãos pagam os serviços diretamente pela app Pagali. As entradas aparecem no Portal > Pagamentos com identificacao do pagador e referencia do serviço.' },
          { titulo: 'Relatorios', conteudo: 'Gera relatorios de arrecadacao por período, tipo de serviço e zona geografica. Exporta em CSV/PDF para auditoria e prestacao de contas.' },
        ],
      },
    ],
  },
  {
    id: 'developer',
    icon: <FiCode />,
    nome: 'Developer',
    subtitulo: 'Para programadores e integradores',
    cor: 'text-green-600',
    corBg: 'bg-green-50 border-green-200',
    descricao: 'Guia técnico para programadores que integram pagamentos Pagali nos seus sistemas, websites e aplicações.',
    seccoes: [
      {
        titulo: 'Integracao API',
        icon: <FiCode />,
        passos: [
          { titulo: 'Criar conta developer', conteudo: 'Regista-te em pagali.cv/developers. Recebes acesso ao ambiente sandbox (teste) com credenciais separadas de producao.' },
          { titulo: 'Gerar chaves API', conteudo: 'No dashboard de developer (Portal > API), gera o teu api_key e api_secret. Guarda o secret num local seguro — nunca o exponhas no frontend.' },
          { titulo: 'Sandbox vs Producao', conteudo: 'Desenvolve e testa no sandbox (sem dinheiro real). Quando estiveres pronto, solicita a migracao para producao com a mesma chave API.' },
        ],
      },
      {
        titulo: 'Endpoints Principais',
        icon: <FiGlobe />,
        passos: [
          { titulo: 'Criar pagamento', conteudo: 'POST /api/v1/payments — Cria um pedido de pagamento com valor, descrição e callback URL. Devolve um link de pagamento que redirecionas o teu cliente.' },
          { titulo: 'Verificar estado', conteudo: 'GET /api/v1/payments/:id — Consulta o estado de um pagamento (pending, paid, failed, cancelled).' },
          { titulo: 'Webhooks', conteudo: 'Configura um webhook URL no dashboard. O Pagali envia um POST ao teu servidor quando o pagamento e confirmado — util para atualizar o estado do pedido automaticamente.' },
          { titulo: 'Documentacao completa', conteudo: 'Toda a documentacao com exemplos em cURL, JavaScript, Python e PHP esta em pagali.cv/programadores.' },
        ],
      },
    ],
  },
];

// ─────────────────── Components ───────────────────

const AccordionItem: React.FC<{ step: Step }> = ({ step }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden transition-all hover:border-gray-200">
      <button
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${open ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
          {open ? <FiCheck className="text-sm" /> : <FiChevronRight className="text-sm" />}
        </div>
        <span className="flex-1 font-medium text-gray-900 text-sm">{step.titulo}</span>
        <FiChevronDown className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pl-14">
          <p className="text-sm text-gray-600 leading-relaxed">{step.conteudo}</p>
        </div>
      )}
    </div>
  );
};

// ─────────────────── Page ───────────────────

const CentralDeAjuda: React.FC = () => {
  const navigate = useNavigate();
  const [selectedProfile, setSelectedProfile] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const profile = selectedProfile ? profiles.find(p => p.id === selectedProfile) : null;

  // Search across all profiles
  const searchResults = searchQuery.trim().length > 2
    ? profiles.flatMap(p =>
        p.seccoes.flatMap(s =>
          s.passos
            .filter(step =>
              step.titulo.toLowerCase().includes(searchQuery.toLowerCase()) ||
              step.conteudo.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map(step => ({ profile: p, section: s, step }))
        )
      )
    : [];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          {selectedProfile && (
            <button
              onClick={() => setSelectedProfile(null)}
              className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <FiArrowLeft className="text-gray-600" />
            </button>
          )}
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
            <FiBookOpen className="text-2xl text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Central de Ajuda</h1>
            <p className="text-gray-500">Manual de utilizador da plataforma Pagali</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Pesquisar no manual... (ex: carregar wallet, criar evento, API)"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSelectedProfile(null); }}
            className="w-full pl-11 pr-4 py-3.5 rounded-2xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
          />
        </div>
      </div>

      {/* Search Results */}
      {searchQuery.trim().length > 2 && (
        <div className="mb-8">
          <p className="text-sm text-gray-500 mb-4">
            {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''} para "{searchQuery}"
          </p>
          {searchResults.length > 0 ? (
            <div className="space-y-3">
              {searchResults.slice(0, 15).map((r, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl p-4 border border-gray-100 hover:border-indigo-200 hover:shadow-sm transition-all cursor-pointer"
                  onClick={() => { setSelectedProfile(r.profile.id); setSearchQuery(''); }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${r.profile.corBg} ${r.profile.cor}`}>
                      {r.profile.nome}
                    </span>
                    <span className="text-xs text-gray-400">{r.section.titulo}</span>
                  </div>
                  <p className="font-medium text-gray-900 text-sm">{r.step.titulo}</p>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{r.step.conteudo}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <FiHelpCircle className="text-3xl text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">Nenhum resultado encontrado. Tenta termos diferentes.</p>
            </div>
          )}
        </div>
      )}

      {/* Profile Grid */}
      {!selectedProfile && searchQuery.trim().length <= 2 && (
        <>
          <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl p-6 md:p-8 text-white mb-8 relative overflow-hidden">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
            <h2 className="text-xl md:text-2xl font-bold mb-2">Bem-vindo ao Manual Pagali</h2>
            <p className="text-white/85 text-sm md:text-base max-w-2xl">
              Seleciona o teu perfil para ver o guia adaptado ao que precisas. Cada seccao tem instrucoes passo a passo com tudo o que precisas saber.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-4">Escolhe o teu perfil</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {profiles.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedProfile(p.id)}
                className={`group relative flex flex-col items-start p-5 rounded-2xl border-2 bg-white transition-all hover:-translate-y-1 hover:shadow-lg text-left ${p.corBg}`}
              >
                <div className={`text-2xl mb-3 ${p.cor}`}>{p.icon}</div>
                <h4 className="font-bold text-gray-900">{p.nome}</h4>
                <p className="text-xs text-gray-500 mt-1">{p.subtitulo}</p>
                <div className="mt-3 flex items-center gap-1 text-xs font-medium text-gray-400 group-hover:text-indigo-600 transition-colors">
                  {p.seccoes.length} seccoes <FiChevronRight />
                </div>
              </button>
            ))}
          </div>

          {/* Quick Contact */}
          <div className="mt-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-5 text-white flex flex-col sm:flex-row items-center gap-4">
            <div className="p-3 bg-white/15 rounded-xl backdrop-blur-sm">
              <FiMessageCircle className="text-2xl" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-semibold">Nao encontras o que procuras?</h3>
              <p className="text-sm text-white/80">Fala com a Lyra, a nossa assistente virtual, ou envia email para suporte@pagali.cv</p>
            </div>
            <button
              onClick={() => window.dispatchEvent(new Event('open-lyra'))}
              className="px-5 py-2.5 bg-white text-indigo-600 rounded-xl font-semibold hover:bg-white/90 transition-colors"
            >
              Abrir Lyra
            </button>
          </div>
        </>
      )}

      {/* Profile Detail */}
      {profile && (
        <div className="space-y-6">
          {/* Profile Header */}
          <div className={`rounded-2xl p-6 border-2 ${profile.corBg}`}>
            <div className="flex items-center gap-4 mb-3">
              <div className={`text-3xl ${profile.cor}`}>{profile.icon}</div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{profile.nome}</h2>
                <p className="text-sm text-gray-500">{profile.subtitulo}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">{profile.descrição}</p>
          </div>

          {/* Table of Contents */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-3">Indice</h3>
            <div className="flex flex-wrap gap-2">
              {profile.seccoes.map((s, idx) => (
                <a
                  key={idx}
                  href={`#section-${idx}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-full text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                >
                  {s.icon}
                  {s.titulo}
                </a>
              ))}
            </div>
          </div>

          {/* Sections */}
          {profile.seccoes.map((section, idx) => (
            <div key={idx} id={`section-${idx}`} className="bg-white rounded-2xl p-5 border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2.5 rounded-xl ${profile.corBg} ${profile.cor}`}>
                  {section.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{section.titulo}</h3>
              </div>
              <div className="space-y-2">
                {section.passos.map((step, i) => (
                  <AccordionItem key={i} step={step} />
                ))}
              </div>
            </div>
          ))}

          {/* Back to profiles */}
          <button
            onClick={() => setSelectedProfile(null)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-indigo-600 transition-colors"
          >
            <FiArrowLeft /> Voltar a lista de perfis
          </button>
        </div>
      )}
    </div>
  );
};

export default CentralDeAjuda;
